import React, { useState } from "react";
import { X, Check, ChevronDown } from "lucide-react";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/hooks/useTenant";
import { useImobiliaria } from "@/hooks/useImobiliaria";
import { useToast } from "@/hooks/use-toast";

interface AlertDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function AlertDrawer({ isOpen, onClose }: AlertDrawerProps) {
  const { subdominio } = useTenant();
  const { data: imobiliaria } = useImobiliaria(subdominio);
  const { toast } = useToast();
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    nome: "",
    email: "",
    telefone: "",
    regiao: "",
    purpose: "Comprar",
    categoria: "residencial" as "residencial" | "comercial",
    area_max: "",
    preco_min: "",
    preco_max: "",
    receberAlertas: true,
  });
  const [honeypot, setHoneypot] = useState("");

  const [selectedPropertyTypes, setSelectedPropertyTypes] = useState<string[]>(
    [],
  );
  const [selectedPropertyFeatures, setSelectedPropertyFeatures] = useState<
    string[]
  >([]);
  const [selectedCondominiumFeatures, setSelectedCondominiumFeatures] =
    useState<string[]>([]);
  const [quartos, setQuartos] = useState("0");
  const [suites, setSuites] = useState("0");
  const [banheiros, setBanheiros] = useState("0");
  const [vagas, setVagas] = useState("0");

  const propertyTypes = [
    "Casa",
    "Sobrado",
    "Apartamento",
    "Cobertura",
    "Studio",
    "Casa de Condomínio",
    "Loft",
    "Terreno",
  ];

  const propertyFeatures = [
    "Ar-condicionado",
    "Aceita animais",
    "Varanda Gourmet",
    "Cozinha americana",
    "Closet",
    "Lareira",
    "Piscina",
    "Mobiliado",
  ];

  const condominiumFeatures = [
    "Academia",
    "Salão de Festas",
    "Elevador",
    "Salão de Jogos",
    "Espaço Gourmet",
    "Jardim",
    "Piscina",
    "Playground",
    "Churrasqueira",
    "Quadra de Squash",
    "Cinema",
    "Bicicletário",
    "Quadra poliesportiva",
    "Coworking",
    "Acesso para deficientes",
    "Sauna",
    "Lavanderia",
    "Spa",
    "Condomínio fechado",
    "Portaria 24h",
    "Portão eletrônico",
  ];

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const togglePropertyType = (type: string) => {
    setSelectedPropertyTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type],
    );
  };

  const togglePropertyFeature = (feature: string) => {
    setSelectedPropertyFeatures((prev) =>
      prev.includes(feature)
        ? prev.filter((f) => f !== feature)
        : [...prev, feature],
    );
  };

  const toggleCondominiumFeature = (feature: string) => {
    setSelectedCondominiumFeatures((prev) =>
      prev.includes(feature)
        ? prev.filter((f) => f !== feature)
        : [...prev, feature],
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validação básica
    if (!formData.nome.trim() || !formData.email.trim()) {
      toast({
        title: "Campos obrigatórios",
        description: "Por favor, preencha seu nome e email.",
        variant: "destructive",
      });
      return;
    }

    if (!imobiliaria?.id) {
      toast({
        title: "Erro",
        description: "Informações da imobiliária não disponíveis.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Mapear finalidade
      let finalidade: "locacao" | "venda" = "venda";
      if (formData.purpose === "Alugar") {
        finalidade = "locacao";
      } else if (formData.purpose === "Todos") {
        // Se "Todos", vamos usar "venda" como padrão ou podemos criar 2 alertas
        finalidade = "venda";
      }

      // Combinar características
      const caracteristicas = [
        ...selectedPropertyFeatures,
        ...selectedCondominiumFeatures,
      ].filter(Boolean);

      // Preparar payload (includes honeypot for bot detection)
      const payload = {
        nome: formData.nome.trim(),
        email: formData.email.trim(),
        telefone: formData.telefone?.trim() || undefined,
        imobiliaria_id: imobiliaria.id,
        _hp_field: honeypot, // Honeypot field for bot detection
        perfil_interesse: {
          regiao: formData.regiao?.trim() || undefined,
          finalidade,
          categoria: formData.categoria,
          tipo_imovel: selectedPropertyTypes[0]?.toLowerCase() || undefined,
          quartos: quartos !== "0" ? parseInt(quartos.replace("+", "")) : undefined,
          suites: suites !== "0" ? parseInt(suites.replace("+", "")) : undefined,
          vagas: vagas !== "0" ? parseInt(vagas.replace("+", "")) : undefined,
          area_max: formData.area_max ? parseInt(formData.area_max) : undefined,
          preco_min: formData.preco_min ? parseFloat(formData.preco_min) : undefined,
          preco_max: formData.preco_max ? parseFloat(formData.preco_max) : undefined,
          caracteristicas: caracteristicas.length > 0 ? caracteristicas : undefined,
        },
      };

      console.log("📤 Enviando alerta:", payload);

      // Chamar edge function
      const { data, error } = await supabase.functions.invoke('criar-alerta-imovel', {
        body: payload,
      });

      if (error) {
        console.error("❌ Erro ao criar alerta:", error);
        throw error;
      }

      console.log("✅ Alerta criado:", data);

      toast({
        title: "Alerta criado!",
        description: "Você receberá notificações quando novos imóveis aparecerem.",
      });

      setSubmitted(true);
    } catch (error) {
      console.error("❌ Erro ao criar alerta:", error);
      toast({
        title: "Erro ao criar alerta",
        description: error instanceof Error ? error.message : "Tente novamente mais tarde.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSubmitted(false);
    setIsSubmitting(false);
    // Reset form
    setFormData({
      nome: "",
      email: "",
      telefone: "",
      regiao: "",
      purpose: "Comprar",
      categoria: "residencial",
      area_max: "",
      preco_min: "",
      preco_max: "",
      receberAlertas: true,
    });
    setSelectedPropertyTypes([]);
    setSelectedPropertyFeatures([]);
    setSelectedCondominiumFeatures([]);
    setQuartos("0");
    setSuites("0");
    setBanheiros("0");
    setVagas("0");
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={handleClose} />

      {/* Drawer */}
      <div className="relative z-10 ml-auto h-full w-full max-w-[575px] bg-white flex flex-col">
        {/* Header */}
        <div className="flex items-center gap-6 p-6 border-b border-gray-200">
          <button onClick={handleClose} className="p-1">
            <X className="w-6 h-6 text-neutral-black" />
          </button>
          <h2 className="text-2xl font-bold text-neutral-black">Criar um alerta</h2>
        </div>

        {/* Content */}
        {!submitted ? (
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          {/* Honeypot field - hidden from users, catches bots */}
          <input
            type="text"
            name="_hp_field"
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
            className="absolute -left-[9999px] opacity-0"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
          />
          <div className="p-6 space-y-8">
            {/* Personal Information */}
            <div className="space-y-6 pb-6 border-b border-gray-200">
              <div className="space-y-1">
                <label className="text-sm text-neutral-black">Nome *</label>
                <input
                  type="text"
                  value={formData.nome}
                  onChange={(e) => handleInputChange("nome", e.target.value)}
                  placeholder="Maria da Silva"
                  required
                  className="w-full px-5 py-3 rounded-full border border-gray-200 bg-white text-neutral-gray-light"
                />
              </div>

              <div className="space-y-1">
                <label className="text-sm text-neutral-black">E-mail *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  placeholder="email@dominio.com"
                  required
                  className="w-full px-5 py-3 rounded-full border border-gray-200 bg-white text-neutral-gray-light"
                />
              </div>

              <div className="space-y-1">
                <label className="text-sm text-neutral-black">
                  Telefone (opcional)
                </label>
                <input
                  type="tel"
                  value={formData.telefone}
                  onChange={(e) =>
                    handleInputChange("telefone", e.target.value)
                  }
                  placeholder="(11) 99999-1111"
                  className="w-full px-5 py-3 rounded-full border border-gray-200 bg-white text-neutral-gray-light"
                />
              </div>

              <div className="space-y-1">
                <label className="text-sm text-neutral-black">
                  Região (opcional)
                </label>
                <input
                  type="text"
                  value={formData.regiao}
                  onChange={(e) => handleInputChange("regiao", e.target.value)}
                  placeholder="Campinas, Barão Geraldo, etc."
                  className="w-full px-5 py-3 rounded-full border border-gray-200 bg-white text-neutral-gray-light"
                />
              </div>
            </div>

            {/* Property Purpose Toggle */}
            <div className="space-y-3 border-b border-gray-200 pb-6">
              <label className="text-sm text-neutral-black">
                Eu quero encontrar um imóvel para
              </label>
              <div className="flex rounded-full border border-gray-200 p-0.5">
                {["Todos", "Alugar", "Comprar"].map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => handleInputChange("purpose", option)}
                    className={`flex-1 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                      formData.purpose === option
                        ? "bg-neutral-gray-bg border border-neutral-gray-dark text-neutral-black"
                        : "text-neutral-gray-light"
                    }`}
                  >
                    {option}
                  </button>
                ))}
              </div>

              {/* Property Types */}
              <div className="space-y-4 pt-4">
                <h3 className="text-sm font-bold text-neutral-black">Tipo</h3>
                <div className="grid grid-cols-2 gap-4">
                  {propertyTypes.map((type) => (
                    <label
                      key={type}
                      className="flex items-center space-x-3 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedPropertyTypes.includes(type)}
                        onChange={() => togglePropertyType(type)}
                        className="w-6 h-6 border-2 border-neutral-black rounded"
                      />
                      <span className="text-base text-neutral-black">{type}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Room Specifications */}
              <div className="grid grid-cols-2 gap-6 pt-4">
                {[
                  { label: "Quartos", value: quartos, setter: setQuartos },
                  { label: "Suítes", value: suites, setter: setSuites },
                  {
                    label: "Banheiros",
                    value: banheiros,
                    setter: setBanheiros,
                  },
                  { label: "Vagas", value: vagas, setter: setVagas },
                ].map(({ label, value, setter }) => (
                  <div key={label} className="space-y-2">
                    <label className="text-sm text-neutral-black">{label} *</label>
                    <Select value={value} onValueChange={setter}>
                      <SelectTrigger className="w-full h-12 rounded-full border border-gray-200 bg-white text-neutral-gray-light px-5">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">0</SelectItem>
                        <SelectItem value="1">1</SelectItem>
                        <SelectItem value="2">2</SelectItem>
                        <SelectItem value="3">3</SelectItem>
                        <SelectItem value="4">4</SelectItem>
                        <SelectItem value="5+">5+</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>

              {/* Area e Preço */}
              <div className="grid grid-cols-2 gap-6 pt-4">
                <div className="space-y-2">
                  <label className="text-sm text-neutral-black">Área máxima (m²)</label>
                  <input
                    type="number"
                    value={formData.area_max}
                    onChange={(e) => handleInputChange("area_max", e.target.value)}
                    placeholder="120"
                    className="w-full px-5 py-3 rounded-full border border-gray-200 bg-white text-neutral-gray-light"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm text-neutral-black">Preço máximo (R$)</label>
                  <input
                    type="number"
                    value={formData.preco_max}
                    onChange={(e) => handleInputChange("preco_max", e.target.value)}
                    placeholder="500000"
                    className="w-full px-5 py-3 rounded-full border border-gray-200 bg-white text-neutral-gray-light"
                  />
                </div>
              </div>

              {/* Property Features */}
              <div className="space-y-4 pt-4">
                <h3 className="text-sm font-bold text-neutral-black">
                  Diferenciais do Imóvel
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  {propertyFeatures.map((feature) => (
                    <label
                      key={feature}
                      className="flex items-center space-x-3 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedPropertyFeatures.includes(feature)}
                        onChange={() => togglePropertyFeature(feature)}
                        className="w-6 h-6 border-2 border-neutral-black rounded"
                      />
                      <span className="text-base text-neutral-black">{feature}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Condominium Features */}
              <div className="space-y-4 pt-4">
                <h3 className="text-sm font-bold text-neutral-black">
                  Diferenciais do Condomínio (Se aplicável)
                </h3>
                <div className="grid grid-cols-2 gap-4 max-h-[300px] overflow-y-auto">
                  {condominiumFeatures.map((feature) => (
                    <label
                      key={feature}
                      className="flex items-center space-x-3 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedCondominiumFeatures.includes(feature)}
                        onChange={() => toggleCondominiumFeature(feature)}
                        className="w-6 h-6 border-2 border-neutral-black rounded"
                      />
                      <span className="text-base text-neutral-black">{feature}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Email Alerts Checkbox */}
            <div className="space-y-6">
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.receberAlertas}
                  onChange={(e) =>
                    handleInputChange(
                      "receberAlertas",
                      e.target.checked.toString(),
                    )
                  }
                  className="w-6 h-6 border-2 border-neutral-black rounded checked:bg-neutral-black checked:border-neutral-black"
                />
                <span className="text-base text-neutral-black">
                  Quero receber alertas por e-mail
                </span>
              </label>
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 flex items-center justify-between">
            <button
              type="button"
              onClick={() => {
                // Reset form
                setFormData({
                  nome: "",
                  email: "",
                  telefone: "",
                  regiao: "",
                  purpose: "Comprar",
                  categoria: "residencial",
                  area_max: "",
                  preco_min: "",
                  preco_max: "",
                  receberAlertas: true,
                });
                setSelectedPropertyTypes([]);
                setSelectedPropertyFeatures([]);
                setSelectedCondominiumFeatures([]);
                setQuartos("0");
                setSuites("0");
                setBanheiros("0");
                setVagas("0");
              }}
              className="text-xl text-neutral-black hover:text-neutral-black/80 transition-colors"
            >
              Limpar filtros
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-8 py-4 bg-neutral-black text-white text-xl rounded-full hover:bg-neutral-black/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? "Criando..." : "Criar alerta"}
            </button>
          </div>
        </form>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center p-8 lg:p-12 text-center gap-6">
            {/* Success Icon */}
            <div className="w-28 h-28 lg:w-32 lg:h-32 rounded-full border-[10px] border-brand-gold bg-neutral-gray-bg flex items-center justify-center">
              <Check
                className="w-12 h-12 lg:w-14 lg:h-14 text-brand-gold"
                strokeWidth={4}
              />
            </div>

            {/* Success Message */}
            <div className="space-y-3 max-w-md">
              <h3 className="text-3xl lg:text-4xl font-black text-neutral-black leading-tight">
                Preferências recebidas!
              </h3>
              <p className="text-lg lg:text-xl text-neutral-gray-light">
                Pronto! Vamos avisar você por e-mail sempre que aparecer um novo imóvel com essas características.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
